import type {DefinitionOrReference} from '@github-ui/code-nav'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Link} from '@github-ui/react-core/link'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {useCodeHTML} from '@github-ui/syntax-highlighted-lines/hooks/use-code-html'
import {Box, Link as PrimerLink} from '@primer/styled-react'
import {useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react'

import {useCurrentBlob} from '../../../../hooks/CurrentBlob'
import {measureMonospaceTextWidth} from '../../../../utilities/measure-text'
import {isHiddenUnicodeShown} from '../../Banners/HiddenUnicode'
import {HighlightedOverlay} from '../HighlightedOverlay'

export function CodeNavCell({
  reference,
  isHighlighted,
  href,
  onClick,
  role,
  ariaLevel,
  symbol,
  index,
  focusElement,
}: {
  reference: DefinitionOrReference
  isHighlighted: boolean
  href: string
  onClick?: () => void
  role?: string
  ariaLevel?: number
  symbol: string
  index?: number
  focusElement?: boolean
}) {
  const useCanvasMeasurement = isFeatureEnabled('code_view_canvas_text_measurement')
  const [offsetWidth, setOffsetWidth] = useState<number | null>(null)
  const text = useMemo(() => getOffsetString(symbol, reference), [symbol, reference])

  // Canvas-based measurement: compute in a layout effect before paint
  useLayoutEffect(() => {
    if (!useCanvasMeasurement) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOffsetWidth(measureMonospaceTextWidth(text))
  }, [useCanvasMeasurement, text])

  // Legacy DOM measurement: read offsetWidth from a hidden span via ref callback
  const measureRef = useCallback(
    (el: HTMLSpanElement | null) => {
      if (useCanvasMeasurement) return
      setOffsetWidth(el?.offsetWidth ?? null)
    },
    [useCanvasMeasurement],
  )

  const sx = isHighlighted
    ? {
        background: 'var(--bgColor-attention-muted, var(--color-attention-subtle))',
        boxShadow: 'inset 2px 0 0 var(--bgColor-attention-emphasis, var(--color-attention-fg))',
      }
    : {}

  useEffect(() => {
    if (focusElement) {
      const element = document.getElementById(`find-in-file-item-${index}`)
      if (element) {
        element.focus()
      }
    }
  }, [focusElement, index])

  const {tabSize} = useCurrentBlob()
  const hiddenUnicodeShown = isHiddenUnicodeShown()
  const codeHtml = useCodeHTML(
    reference.highlightedText,
    reference.stylingDirectives,
    reference.bodyText,
    'plain',
    tabSize,
    hiddenUnicodeShown,
  )

  return (
    <PrimerLink
      as={Link}
      to={href}
      role={role}
      sx={{
        display: 'block',
        p: 0,
        fontWeight: 400,
        fontSize: '12px',
        ':hover:not([disabled])': {bg: 'canvas.default'},
        ':hover': {textDecoration: 'none'},
        '[data-component="text"]': {gridArea: 'auto'},
        gridTemplateAreas: 'text',
        whiteSpace: 'break-spaces',
        verticalAlign: 'top',
        lineHeight: '20px',
        position: 'relative',
        overflowWrap: 'anywhere',
        fontFamily: 'mono',
        overflow: 'visible',
      }}
      onClick={ev => {
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (!ev.ctrlKey && !ev.metaKey) {
          onClick?.()
        }
      }}
      onSelect={onClick}
      id={`find-in-file-item-${index}`}
      aria-current={isHighlighted ? 'location' : undefined}
      aria-level={ariaLevel}
      onKeyDown={e => {
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (e.key === 'ArrowDown') {
          focusSibling('nextElementSibling')
          e.preventDefault()
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        } else if (e.key === 'ArrowUp') {
          focusSibling('previousElementSibling')
          e.preventDefault()
        }
      }}
    >
      <Box
        sx={{
          p: 1,
          py: '5px',
          ...sx,
        }}
      >
        <div className="d-flex">
          <Box className="text-small blob-num color-fg-muted" sx={{width: 'auto', minWidth: 'auto'}}>
            {reference.lineNumber}
          </Box>
          <Box sx={{overflow: 'hidden', whiteSpace: 'pre', position: 'relative'}}>
            {offsetWidth !== null && (
              <div id={`offset-${reference.href(false)}`} style={{marginLeft: -offsetWidth}}>
                {symbol.length > 0 && (
                  <HighlightedOverlay
                    symbols={[reference]}
                    lineNumber={reference.lineNumber}
                    sx={{overflow: 'initial'}}
                    isNotUsingWhitespace
                  />
                )}
                <VerifiedHTMLText
                  sx={{
                    position: 'relative',
                    width: '100%',
                    overflow: 'hidden',
                  }}
                  html={codeHtml}
                  aria-current={isHighlighted ? 'location' : undefined}
                />
              </div>
            )}
            {/* Legacy: hidden span used to measure offset in px when canvas measurement is disabled */}
            {!useCanvasMeasurement && (
              <span ref={measureRef} style={{visibility: 'hidden', position: 'absolute', whiteSpace: 'pre'}}>
                {text}
              </span>
            )}
          </Box>
        </div>
      </Box>
    </PrimerLink>
  )
}

const TOTAL_ALLOWED_CHARS = 34
export function getOffsetString(searchTerm: string, reference: DefinitionOrReference) {
  let canFitLength = TOTAL_ALLOWED_CHARS - searchTerm.length
  const substringBeforeMatch = reference.bodyText.slice(0, reference.ident.start.column)
  if (canFitLength <= 0) {
    // Fully offset to fit the match
    return substringBeforeMatch
  }

  // Maximize spacing based on the string after match
  const substringAfterMatch = reference.bodyText.slice(reference.ident.start.column + searchTerm.length).trimEnd()
  canFitLength = Math.max(canFitLength / 2, canFitLength - substringAfterMatch.length)
  const wordsOffset = substringBeforeMatch.split(' ')
  const fit: string[] = []

  for (let index = wordsOffset.length - 1; index >= 0; index--) {
    const word = wordsOffset[index]!
    fit.unshift(word)

    if (fit.join(' ').length <= canFitLength) {
      wordsOffset.pop()
    } else {
      break
    }
  }

  const offset = wordsOffset.join(' ')
  // If fitting strings starts from the whitespace(s) or tabs, add these characters to the offset as well.
  const whitespaceOffset = (substringBeforeMatch.slice(offset.length).match(/^[ \t]*/) || [])[0] ?? ''

  return `${wordsOffset.join(' ')}${whitespaceOffset}`
}

export function focusSibling(key: 'nextElementSibling' | 'previousElementSibling') {
  const {activeElement} = document
  const elementSibling = activeElement?.[key] as HTMLInputElement | null
  if (elementSibling) {
    if (elementSibling.role !== 'treeitem' && key === 'nextElementSibling') {
      // There is a group element that we don't want to focus between
      // the first level (expandable) and the second level
      const treeItem = elementSibling.querySelector<HTMLElement>('[role="treeitem"]')
      treeItem?.focus()
    } else {
      elementSibling.focus()
    }
  }
}
